import { useEffect, useState } from "react";

/**
 * Detects when a newer build of the app has been deployed than the one the
 * user currently has loaded, so we can prompt them to refresh.
 *
 * Mechanism (mirrors the announcement poller):
 *  - `__APP_VERSION__` is baked into this bundle at build time from
 *    package.json (see vite.config.ts).
 *  - The same version is written to `/version.json`, which nginx serves with
 *    `Cache-Control: no-store` so every fetch is fresh.
 *  - We poll that file (and re-check on window focus). When the served version
 *    differs from the baked one, a new release is live and `updateAvailable`
 *    flips to true.
 *
 * Failures (offline, 404 in local dev where no version.json exists) are
 * swallowed — we never want a flaky network to nag the user.
 */

const POLL_INTERVAL_MS = 60_000;

// Baked at build time. Falls back to 'dev' under plain `tsc`/tests where the
// define isn't applied, which disables the check (we never prompt in dev).
const LOADED_VERSION =
  typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "dev";

interface VersionManifest {
  version?: string;
}

export function useAppUpdate() {
  const [releasedVersion, setReleasedVersion] = useState<string | null>(null);

  useEffect(() => {
    // Skip entirely in local dev — there is no deployed version.json to compare
    // against and the loaded version is whatever package.json says.
    if (LOADED_VERSION === "dev") return;

    let cancelled = false;

    const check = async () => {
      try {
        const res = await fetch("/version.json", { cache: "no-store" });
        if (!res.ok) return;
        const data = (await res.json()) as VersionManifest;
        if (!cancelled && typeof data.version === "string" && data.version) {
          setReleasedVersion(data.version);
        }
      } catch {
        /* offline or missing — ignore, try again next tick */
      }
    };

    check();
    const interval = window.setInterval(check, POLL_INTERVAL_MS);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const updateAvailable =
    LOADED_VERSION !== "dev" &&
    releasedVersion !== null &&
    releasedVersion !== LOADED_VERSION;

  return { updateAvailable, loadedVersion: LOADED_VERSION, releasedVersion };
}
